import type { CreasePattern } from '@/origami/types';
import { resolveTargetAngle } from '@/origami/angles';
import { toDegrees } from '@/constants/numeric';
import type { FoldFile } from './types';
import { FOLD_CREATOR, FOLD_SPEC_VERSION } from './types';

export interface FoldExportOptions {
  /** Optional folded-state vertex positions (xyz triples per pattern vertex). */
  foldedPositions?: Float32Array | Float64Array | null;
  /** Measured fold angles per edge (radians) to write into the folded frame. */
  measuredFoldAngles?: Float32Array | Float64Array | null;
  /** Extra top-level fields preserved from an import. */
  preserved?: Record<string, unknown>;
}

/**
 * CreasePattern -> FOLD file.
 *
 * Frame 0 is the flat crease pattern, tagged `creasePattern`. If folded
 * geometry is supplied, it is written as a child frame tagged `foldedForm` with
 * `frame_inherit`, which is the idiomatic way to ship both states in one file.
 */
export function patternToFold(
  pattern: CreasePattern,
  options: FoldExportOptions = {},
): FoldFile {
  const edgesFoldAngle = pattern.edgesAssignment.map((a, e) => {
    if (a === 'B' || a === 'C') return null;
    return round(toDegrees(resolveTargetAngle(a, pattern.edgesFoldAngle[e])));
  });

  const file: FoldFile = {
    file_spec: FOLD_SPEC_VERSION,
    file_creator: FOLD_CREATOR,
    file_classes: ['singleModel'],
    ...(options.preserved ?? {}),
    ...(pattern.meta.author ? { file_author: pattern.meta.author } : {}),
    ...(pattern.meta.title ? { file_title: pattern.meta.title } : {}),
    ...(pattern.meta.description ? { file_description: pattern.meta.description } : {}),
    frame_title: pattern.meta.title ?? 'Crease pattern',
    frame_classes: ['creasePattern'],
    frame_attributes: ['2D'],
    ...(pattern.meta.unit ? { frame_unit: pattern.meta.unit } : {}),
    vertices_coords: pattern.verticesCoords.map((v) => [round(v.x), round(v.y)]),
    edges_vertices: pattern.edgesVertices.map((e) => [e[0], e[1]] as [number, number]),
    edges_assignment: pattern.edgesAssignment.slice(),
    edges_foldAngle: edgesFoldAngle,
    faces_vertices: pattern.facesVertices.map((f) => f.slice()),
  };

  if (options.foldedPositions && options.foldedPositions.length >= pattern.verticesCoords.length * 3) {
    const pos = options.foldedPositions;
    const measured = options.measuredFoldAngles;
    file.file_frames = [
      {
        frame_parent: 0,
        frame_inherit: true,
        frame_classes: ['foldedForm'],
        frame_attributes: ['3D'],
        frame_title: `${pattern.meta.title ?? 'Model'} (folded)`,
        vertices_coords: pattern.verticesCoords.map((_, i) => [
          round(pos[i * 3]),
          round(pos[i * 3 + 1]),
          round(pos[i * 3 + 2]),
        ]),
        ...(measured
          ? {
              edges_foldAngle: Array.from(
                { length: pattern.edgesVertices.length },
                (_, e) => (Number.isFinite(measured[e]) ? round(toDegrees(measured[e])) : null),
              ),
            }
          : {}),
      },
    ];
  }

  return file;
}

export const serializeFold = (pattern: CreasePattern, options?: FoldExportOptions): string =>
  JSON.stringify(patternToFold(pattern, options), null, 1);

function round(x: number): number {
  if (!Number.isFinite(x)) return 0;
  // 9 significant decimals is well inside double precision and keeps files small.
  return Math.round(x * 1e9) / 1e9;
}
